import React, { useEffect, useState } from 'react'
import { Card, Table } from 'reactstrap'
import MyPagination from '../pagination'
import { useLoader } from '../../_helper/LoaderContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from 'lucide-react';
import { collectionList } from './service/collection-service';
import { Tooltip } from 'react-tooltip';
import { useLab } from '../../_helper/PharmacyProvider';
import { useCurrency } from '../../_helper/currencyContext';
import DatePicker from "../Common/Component/DatePicker";
import SearchFilterButtons from "../SearchFilterButtons";
import { FormLabel } from 'react-bootstrap';
import { formatDoctorDisplayDateTime, toApiDateString } from '../../utils/dateTime/dateFormat';

export default function CollectionHome() {
    const { t } = useTranslation()
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();
    const [collection, setCollection] = useState([]);
    const { currentLab } = useLab();
    const [startDate, setStartDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");

    const { getCurrencySymbol } = useCurrency()

    useEffect(() => {
        getList()
    }, [currentPage, entriesPerPage])

    const getList = async (
        query = appliedSearch,
        fromDate = appliedStartDate,
        toDate = appliedEndDate
    ) => {
        setLoading(true)
        try {
            const form_data = new FormData()
            form_data.append("search", query);
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);
            if (currentLab?.isAdmin !== true) {
                form_data.append("cashierId", currentLab?._id);
            }
            const apiStartDate = toApiDateString(fromDate);
            const apiEndDate = toApiDateString(toDate);
            if (apiStartDate) {
                form_data.append("startDate", apiStartDate);
            }
            if (apiEndDate) {
                form_data.append("endDate", apiEndDate);
            }
            const res = await collectionList(form_data);
            if (res.data.success == true) {
                setCollection(res.data.data.list)
                setTotalEntries(res.data.data.total || 0);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        const query = search.trim();
        setAppliedSearch(query);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        if (currentPage === 1) {
            getList(query, startDate, endDate);
        } else {
            setCurrentPage(1);
        }
    };

    const handleClear = () => {
        setSearch("");
        setStartDate("");
        setEndDate("");
        const hadFilter = Boolean(appliedSearch || appliedStartDate || appliedEndDate);
        setAppliedSearch("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        if (!hadFilter) return;
        if (currentPage === 1) {
            getList("", "", "");
        } else {
            setCurrentPage(1);
        }
    };

    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    const formatDate = (dateString) => formatDoctorDisplayDateTime(dateString, currentLab) || "-";

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setEntriesPerPage(newSize);  // Use the correct setter
        setCurrentPage(1);           // Reset to page 1
    }

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <div className="input-group" style={{ flex: "1.2 1 240px", minWidth: 200, height: 42 }}>
                            <span className="input-group-text tw-bg-white" id="basic-addon1" style={{ height: 42 }}><SearchIcon /></span>
                            <input
                                type="text"
                                className="form-control icon_form"
                                placeholder={t("Search By Name")}
                                aria-label={t("Search By Name")}
                                aria-describedby="basic-addon1"
                                style={{ borderLeft: 'none', height: 42 }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </div>
                        <div style={{ flex: "1 1 210px", minWidth: 190 }} title={t("Start Date")}>
                            <DatePicker
                                id="collection-start-date"
                                name="startDate"
                                value={startDate}
                                onChange={(event) => {
                                    setStartDate(event.target.value || "");
                                    setEndDate("");
                                }}
                                isClearable
                                wrapperClassName="filter-field-42"
                                style={{ height: 42, width: "100%" }}
                            />
                        </div>
                        <div style={{ flex: "1 1 210px", minWidth: 190 }} title={t("End Date")}>
                            <DatePicker
                                id="collection-end-date"
                                name="endDate"
                                value={endDate}
                                onChange={(event) => setEndDate(event.target.value || "")}
                                minDate={startDate || undefined}
                                disabled={!startDate}
                                isClearable
                                wrapperClassName="filter-field-42"
                                style={{ height: 42, width: "100%" }}
                            />
                        </div>
                        <div className="ms-lg-auto flex-shrink-0">
                            <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} height={42} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="recent-table table-responsive currency-table tw-rounded-lg tw-border tw-border-black custom-slim-scrollbar"
                                style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
                                <Table className="mb-0 table-center table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th className="bg-primary text-white text-left" style={{ width: '14%' }}><b>{t("Name")}</b></th>
                                            <th className="bg-primary text-white text-left" style={{ width: '10%' }}><b>{t("Login Time")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: '9%' }}><b>{t("Logout Time")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: '9%' }}><b>{t("Opening Amount")}</b></th>
                                            <th className="bg-primary text-white" style={{ width: '16%' }}><b>{t("Earning Amount")}</b></th>
                                            {/* <th className="bg-primary text-white" style={{ width: '10%' }}><b>Balance Amount</b></th> */}
                                            {/* <th className="bg-primary text-white" style={{ width: '10%' }}><b>Status</b></th> */}
                                            {/* <th className="bg-primary text-white text-center" style={{ width: '11%' }}><b>Action</b></th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {collection.map((item, index) => (
                                            <tr key={item._id} className={index % 2 === 0 ? '' : ''}>

                                                <td className="tw-px-3 ps-2 tw-py-3" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    <div
                                                        className="tw-font-medium tw-text-gray-900 tw-ps-3"
                                                    >
                                                    </div>
                                                    {item?.cashierName}
                                                </td>
                                                <td>
                                                    {formatDate(item?.shiftStartTime) || "---"}
                                                </td>
                                                <td>
                                                    {formatDate(item?.shiftEndTime) || "---"}
                                                </td>
                                                <td className='tw-font-bold'>
                                                    {getCurrencySymbol(currentLab.baseCurrency)}{item?.openingAmount[currentLab.baseCurrency]?.toFixed(2) || "0.00"}
                                                </td>
                                                <td className='tw-font-bold'>
                                                    {getCurrencySymbol(currentLab.baseCurrency)}{item?.closingAmount[currentLab.baseCurrency]?.toFixed(2) || "0.00"}
                                                </td>
                                                {/* <td className="tw-px-4 tw-py-4 text-center">
                                                    <div className="">
                                                        <Tooltip id="view" place="top" content="view" />
                                                        <button className='success-action-button me-1' data-tooltip-id="view">
                                                            <i className="fa fa-eye tw-text-red-600" style={{ fontSize: "18px" }}></i>
                                                        </button>
                                                    </div>
                                                </td> */}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-md-flex align-items-center text-center justify-content-between mt-3 mb-3">
                                <div className="show-entries">
                                    <FormLabel>{t("Show")}&nbsp;</FormLabel>
                                    <select style={{ display: 'unset', width: 'auto', padding: '0.375rem 2.25rem 0.375rem 0.75rem' }} value={entriesPerPage}
                                        onChange={handlePageSizeChange} className="form-control">
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={30}>30</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>&nbsp;
                                    <FormLabel>{t("entries")}</FormLabel>
                                </div>
                                <MyPagination totalPages={totalPages} currentPage={currentPage} onPageChange={(page) => setCurrentPage(page)} />
                            </div>
                            {/* <div className="mt-3 mb-3">
                                <MyPagination totalPages={totalPages}
                                    currentPage={currentPage}
                                    onPageChange={(page) => setCurrentPage(page)}></MyPagination>
                            </div> */}
                        </div>
                    </div>
                </div>
            </Card>
        </>
    )
}
